import {
  AfterViewInit,
  ContentChildren,
  Directive,
  EventEmitter,
  OnDestroy,
  Output,
  QueryList
} from '@angular/core';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Subscription } from 'rxjs';
import { NxReorderItemDirective } from './nx-reorder-item.directive';

/** Indices of a reorder, ready to feed into `moveItemInArray(arr, previousIndex, currentIndex)`. */
export interface NxReorderEvent {
  previousIndex: number;
  currentIndex: number;
}

/**
 * Lightweight vertical single-list reordering built on pragmatic-drag-and-drop.
 *
 * Mental model mirrors the CDK it replaces:
 * - `[nxReorderList]` on the container (was `cdkDropList`)
 * - `[nxReorderItem]` on each `*ngFor` row (was `cdkDrag`)
 * - `[nxReorderHandle]` on the grip element (was `cdkDragHandle`, optional)
 *
 * It never touches your data — it only emits `(nxReorder)` with indices, so the
 * consumer stays in control of how to apply them (mutating array or immutable/signal copy).
 */
@Directive({
  selector: '[nxReorderList]'
})
export class NxReorderListDirective implements AfterViewInit, OnDestroy {
  @Output() nxReorder = new EventEmitter<NxReorderEvent>();

  @ContentChildren(NxReorderItemDirective) private items!: QueryList<NxReorderItemDirective>;

  private cleanup: (() => void) | null = null;
  private changesSub?: Subscription;

  ngAfterViewInit(): void {
    this.register();
    // re-register whenever the rendered rows change (reorder / add / remove)
    this.changesSub = this.items.changes.subscribe(() => this.register());
  }

  ngOnDestroy(): void {
    this.changesSub?.unsubscribe();
    this.cleanup?.();
  }

  private register(): void {
    this.cleanup?.();
    const handlers: (() => void)[] = [];

    this.items.toArray().forEach((row, index) => {
      if (row.nxReorderItemDisabled) {
        return;
      }
      const element = row.elementRef.nativeElement;
      handlers.push(
        draggable({
          element,
          dragHandle: row.dragHandleElement,
          getInitialData: () => ({ index }),
          onDragStart: () => element.classList.add('nx-reorder-dragging'),
          onDrop: () => {
            element.classList.remove('nx-reorder-dragging');
            this.suppressPostDropClick();
          }
        }),
        dropTargetForElements({
          element,
          canDrop: ({ source }) => typeof source.data?.['index'] === 'number',
          getData: ({ input }) =>
            attachClosestEdge({ index }, { element, input, allowedEdges: ['top', 'bottom'] }),
          onDragEnter: ({ source, self }) => this.showDropEdge(element, source.element, self.data),
          onDrag: ({ source, self }) => this.showDropEdge(element, source.element, self.data),
          onDragLeave: () => this.clearDropEdge(element),
          onDrop: ({ source, self }) => {
            this.clearDropEdge(element);
            const previousIndex = source.data['index'] as number;
            const overIndex = self.data['index'] as number;
            let currentIndex = extractClosestEdge(self.data) === 'bottom' ? overIndex + 1 : overIndex;
            // the dragged row is removed before re-insertion, so shift the target down by one
            if (previousIndex < currentIndex) {
              currentIndex--;
            }
            if (previousIndex === currentIndex) {
              return;
            }
            this.nxReorder.emit({ previousIndex, currentIndex });
          }
        })
      );
    });

    this.cleanup = handlers.length ? combine(...handlers) : null;
  }

  /**
   * Shows a drop-indicator line at the top or bottom edge of the hovered row,
   * based on the closest edge computed by the hitbox. Never indicates on the
   * dragged row itself.
   */
  private showDropEdge(element: HTMLElement, sourceElement: Element, targetData: Record<string | symbol, unknown>): void {
    if (sourceElement === element) {
      this.clearDropEdge(element);
      return;
    }
    const edge = extractClosestEdge(targetData);
    element.classList.toggle('nx-reorder-over-top', edge === 'top');
    element.classList.toggle('nx-reorder-over-bottom', edge === 'bottom');
  }

  private clearDropEdge(element: HTMLElement): void {
    element.classList.remove('nx-reorder-over-top', 'nx-reorder-over-bottom');
  }

  /**
   * Native HTML5 drag emits a stray `click` at the drop point right after `dragend`
   * (pragmatic-dnd parks a tiny "honey pot" element there). That click lands on the body,
   * outside any enclosing `p-overlayPanel`, so PrimeNG treats it as an outside-click and
   * closes the panel — which would stop the user from reordering several rows in a row.
   * We swallow exactly that one trailing click (capture phase, short-lived).
   */
  private suppressPostDropClick(): void {
    let timer: number;
    const cleanup = () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', onClick, true);
    };
    const onClick = (event: Event) => {
      event.stopPropagation();
      cleanup();
    };
    document.addEventListener('click', onClick, true);
    // self-disarm if the stray click never comes, so real clicks stay unaffected
    timer = window.setTimeout(cleanup, 300);
  }
}
